import { useState } from "react";
import { Range, DateRangePicker } from "react-date-range";
import { AuditLogMenuActiveIcon } from "../../../../assets/sidemenu-icons";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import { AuditLog } from "../../../../model/audit-log";
import { formatTimestamp } from "../../../../utils";
import { useGetAuditLogsQuery } from "../../../../service/audit-log-service";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import Button from "../../../../common/components/xepp/button/Button";
import { FilterIcon2 } from "../../../../assets/icons";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import styles from "./AuditLog.module.scss";

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

export const AuditLogList = () => {
    const [Page, setPage] = useState(1);
    const PageSize = 10;

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [dateRange, setDateRange] = useState<Range>({
        startDate: undefined,
        endDate: undefined,
        key: "selection",
    });
    const [appliedFilters, setAppliedFilters] = useState<{
        from?: string;
        to?: string;
    }>({
        from: undefined,
        to: undefined,
    });

    const maxDate = new Date();

    const { data, isLoading, isFetching } = useGetAuditLogsQuery({
        Page,
        PageSize,
        from: appliedFilters.from,
        to: appliedFilters.to,
    });

    const count = data?.count ?? 0;

    const handleOpenFilter = () => {
        setIsFilterOpen(true);
    };

    const handleCloseFilter = () => {
        setIsFilterOpen(false);
    };

    const handleApplyFilter = () => {
        setAppliedFilters({
            from: dateRange.startDate ? dateRange.startDate.toISOString() : undefined,
            to: dateRange.endDate ? dateRange.endDate.toISOString() : undefined,
        });

        setIsFilterOpen(false);
        setPage(1);
    };

    const handleResetFilter = () => {
        setDateRange({
            startDate: undefined,
            endDate: undefined,
            key: "selection",
        });

        setAppliedFilters({
            from: undefined,
            to: undefined,
        });

        setIsFilterOpen(false);
        setPage(1);
    };

    const handleDateRangeChange = (ranges: any) => {
        setDateRange(ranges.selection);
    };

    const columns: Array<ColumnType<AuditLog>> = [
        {
            accessor: "timestamp",
            header: "Date",
            render: (record: AuditLog) => <span>{formatTimestamp(record.timestamp)}</span>,
        },
        {
            accessor: "actionDescription",
            header: "Activity",
            render: (record: AuditLog) => <span>{record.actionDescription}</span>,
        },
        {
            accessor: "user",
            header: "User",
            render: (record: AuditLog) => (
                <span>
                    {record.user.firstName} {record.user.lastName}
                </span>
            ),
        },
        {
            accessor: "ip",
            header: "IP Address",
            render: (record: AuditLog) => <span>{record.ip}</span>,
        },
    ];

    const headerActions = (
        <div className={styles.headerActions}>
            <Button 
                leftIcon={<FilterIcon2 />} 
                text="Date Filter" 
                variant="outline" 
                type="button" 
                onClick={handleOpenFilter} 
            />
            {(appliedFilters.from || appliedFilters.to) && (
                <>
                    <span className={styles.filterStatus}>
                        Filtered by date range
                    </span>
                    <Button
                        text="Clear"
                        variant="text"
                        onClick={handleResetFilter}
                    />
                </>
            )}
        </div>
    );

    return (
        <>
            {isFilterOpen && (
                <CommonDialog noPadding>
                    <div className={styles.dialogContainer}>
                        <div className={styles.dialogHeader}>
                            <h2 className={styles.dialogTitle}>Select Date Range</h2>
                            <button
                                onClick={handleCloseFilter}
                                className={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.dateRangeWrapper}>
                            <DateRangePicker
                                ranges={[dateRange]}
                                onChange={handleDateRangeChange}
                                moveRangeOnFirstSelection={false}
                                months={1}
                                direction="horizontal"
                                maxDate={maxDate}
                                showDateDisplay={false}
                                rangeColors={['#007bff']}
                            />
                        </div>

                        <div className={styles.dialogActions}>
                            <Button
                                text="Reset"
                                variant="outline"
                                onClick={handleResetFilter}
                            />
                            <Button
                                text="Apply Filter"
                                onClick={handleApplyFilter}
                            />
                        </div>
                    </div>
                </CommonDialog>
            )}

            <DataTable<AuditLog>
                data={data?.items ?? []}
                columns={columns}
                loading={isLoading}
                isUpdating={isFetching}
                headerActions={headerActions}
                emptyComponent={<EmptyState icon={<AuditLogMenuActiveIcon />} title="No logs recorded yet" />}
            />
            
            <TablePagination 
                isLoading={isFetching} 
                totalItems={count} 
                itemsPerPage={PageSize} 
                currentPage={Page} 
                onPageChange={setPage} 
            />
        </>
    );
};